import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { sharedUiTranslations } from '../../translations';
import { Icon } from '../Icon';
import { Loading } from '../Loading';
import { Tooltip } from '../Tooltip';
import styles from './PinCreationModal.module.css';
const PIN_LENGTH = 6;
const hasThreeConsecutiveSameDigits = (value) => {
    return /(.)\1{2}/.test(value);
};
const hasSequentialDigits = (value) => {
    if (value.length < 3)
        return false;
    const numbersArray = Array.from(value, Number);
    for (let i = 0; i <= numbersArray.length - 3; i++) {
        if (numbersArray[i] + 1 === numbersArray[i + 1] && numbersArray[i + 1] + 1 === numbersArray[i + 2]) {
            return true;
        }
        if (numbersArray[i] - 1 === numbersArray[i + 1] && numbersArray[i + 1] - 1 === numbersArray[i + 2]) {
            return true;
        }
    }
    return false;
};
const isInvalidPin = (value) => {
    return hasThreeConsecutiveSameDigits(value) || hasSequentialDigits(value);
};
function PinCreationModalBase({ isOpen, onConfirm, onClose, loading = false, 'data-testid': dataTestId = 'pin-creation-modal', }) {
    const { t } = useTranslation();
    const [step, setStep] = useState('form');
    const [pin, setPin] = useState(Array(PIN_LENGTH).fill(''));
    const [confirmPin, setConfirmPin] = useState(Array(PIN_LENGTH).fill(''));
    const [pinError, setPinError] = useState('');
    const [generalError, setGeneralError] = useState('');
    const [confirmPinError, setConfirmPinError] = useState('');
    const pinRefs = useRef([]);
    const confirmPinRefs = useRef([]);
    const pinValue = pin.join('');
    const confirmPinValue = confirmPin.join('');
    const handleModalEntered = useCallback(() => {
        pinRefs.current[0]?.focus();
    }, []);
    const validatePin = useCallback((value, isConfirm) => {
        if (value.length < PIN_LENGTH)
            return '';
        if (isInvalidPin(value))
            return t('pin-creation.sequence-error');
        if (isConfirm && value !== pinValue)
            return t('pin-creation.mismatch-error');
        return '';
    }, [pinValue, t]);
    const handlePinDigitChange = useCallback((index, value, isConfirm) => {
        if (step === 'error') {
            setStep('form');
            setGeneralError('');
        }
        const digit = value.replace(/\D/g, '').slice(-1);
        const currentArray = isConfirm ? [...confirmPin] : [...pin];
        currentArray[index] = digit;
        if (isConfirm) {
            setConfirmPin(currentArray);
        }
        else {
            setPin(currentArray);
        }
        const newValue = currentArray.join('');
        const error = validatePin(newValue, isConfirm);
        if (isConfirm) {
            setConfirmPinError(error);
        }
        else {
            setPinError(error);
        }
        const refs = isConfirm ? confirmPinRefs : pinRefs;
        if (digit && index < PIN_LENGTH - 1) {
            refs.current[index + 1]?.focus();
        }
        if (!isConfirm && newValue.length === PIN_LENGTH && !error) {
            setTimeout(() => confirmPinRefs.current[0]?.focus(), 100);
        }
    }, [pin, confirmPin, step, validatePin]);
    const handleKeyDown = useCallback((index, e, isConfirm) => {
        const refs = isConfirm ? confirmPinRefs : pinRefs;
        const currentArray = isConfirm ? confirmPin : pin;
        if (e.key === 'Backspace') {
            if (!currentArray[index] && index > 0) {
                refs.current[index - 1]?.focus();
            }
        }
        else if (e.key === 'ArrowLeft' && index > 0) {
            refs.current[index - 1]?.focus();
        }
        else if (e.key === 'ArrowRight' && index < PIN_LENGTH - 1) {
            refs.current[index + 1]?.focus();
        }
    }, [pin, confirmPin]);
    const handlePaste = useCallback((e, isConfirm) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
        if (!pastedData)
            return;
        const newArray = Array(PIN_LENGTH).fill('');
        for (let i = 0; i < pastedData.length; i++) {
            newArray[i] = pastedData[i];
        }
        const error = validatePin(pastedData, isConfirm);
        if (isConfirm) {
            setConfirmPin(newArray);
            setConfirmPinError(error);
        }
        else {
            setPin(newArray);
            setPinError(error);
            if (pastedData.length === PIN_LENGTH && !error) {
                setTimeout(() => confirmPinRefs.current[0]?.focus(), 100);
            }
        }
    }, [validatePin]);
    const isFormValid = useMemo(() => {
        return (pinValue.length === PIN_LENGTH &&
            confirmPinValue.length === PIN_LENGTH &&
            !pinError &&
            !confirmPinError &&
            pinValue === confirmPinValue);
    }, [pinValue, confirmPinValue, pinError, confirmPinError]);
    const handleSubmit = useCallback(async () => {
        if (!isFormValid)
            return;
        setStep('loading');
        setGeneralError('');
        try {
            await onConfirm(pinValue);
            setStep('success');
        }
        catch (error) {
            setStep('error');
            setGeneralError(t('pin-creation.error'));
        }
    }, [isFormValid, pinValue, onConfirm, t]);
    const isLoading = step === 'loading' || loading;
    const pinRulesTooltip = `${t('pin-creation.rules.item1')} ${t('pin-creation.rules.item2')}`;
    const renderPinInput = (isConfirm) => {
        const currentArray = isConfirm ? confirmPin : pin;
        const refs = isConfirm ? confirmPinRefs : pinRefs;
        const error = isConfirm ? confirmPinError : pinError;
        const label = isConfirm ? t('pin-creation.confirm-pin-label') : t('pin-creation.pin-label');
        const testIdPrefix = isConfirm ? 'confirm-pin' : 'pin';
        return (_jsxs("div", { className: styles.pinField, children: [_jsxs("div", { className: styles.pinLabel, children: [_jsx("span", { className: styles.pinLabelText, children: label }), _jsx(Tooltip, { content: pinRulesTooltip, position: 'right', children: _jsx(Icon, { variant: 'mask', name: 'information', tintColor: '#1F1F1F', alt: 'Regras do PIN', className: styles.infoIcon }) })] }), _jsx("div", { className: styles.pinInputContainer, children: currentArray.map((digit, index) => (_jsx("input", { ref: el => {
                            refs.current[index] = el;
                        }, value: digit, maxLength: 1, type: 'text', pattern: '[0-9]*', autoComplete: 'off', inputMode: 'numeric', onPaste: e => handlePaste(e, isConfirm), onKeyDown: e => handleKeyDown(index, e, isConfirm), "data-testid": `${dataTestId}-${testIdPrefix}-digit-${index}`, disabled: isLoading || (step !== 'form' && step !== 'error'), "aria-label": `${label} ${t('pin-creation.digit-label')} ${index + 1}`, onChange: e => handlePinDigitChange(index, e.target.value, isConfirm), className: `${styles.pinDigit} ${error ? styles.error : ''} ${digit ? styles.filled : ''}` }, `${testIdPrefix}-${index}`))) }), error && (_jsxs("div", { className: styles.errorMessage, "data-testid": `${dataTestId}-${testIdPrefix}-error`, children: [_jsx(Icon, { name: 'inputError', className: styles.errorIcon, alt: t('pin-creation.error-aria-label') }), _jsx("p", { className: styles.errorText, children: error })] }))] }));
    };
    const renderStepContent = () => {
        switch (step) {
            case 'loading':
                return (_jsxs("div", { className: styles.loadingContent, children: [_jsx(Loading, { size: 'large' }), _jsx("p", { className: styles.loadingText, children: t('pin-creation.loading') })] }));
            case 'success':
                return (_jsxs("div", { className: styles.successContent, "data-testid": `${dataTestId}-success`, children: [_jsx("div", { className: styles.successIconWrapper, children: _jsx(Icon, { name: 'checkMark', className: styles.successIcon, alt: 'Sucesso', variant: 'mask', tintColor: '#D64000' }) }), _jsx("h2", { className: styles.successTitle, children: t('pin-creation.success.title') }), _jsx("span", { className: styles.successDescriptionPart1, children: t('pin-creation.success.description-part1') }), _jsx("span", { className: styles.successDescriptionPart2, children: t('pin-creation.success.description-part2') }), _jsx("button", { type: 'button', onClick: onClose, className: styles.confirmButton, "data-testid": `${dataTestId}-success-button`, children: t('pin-creation.success.button') })] }));
            case 'error':
            case 'form':
            default:
                return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.header, children: [_jsx("h2", { className: styles.title, children: t('pin-creation.title') }), _jsx("p", { className: styles.description, children: t('pin-creation.description') })] }), _jsxs("div", { className: styles.pinFieldsContainer, children: [renderPinInput(false), renderPinInput(true)] }), generalError && (_jsxs("div", { className: styles.generalErrorMessage, "data-testid": `${dataTestId}-general-error`, children: [_jsx(Icon, { name: 'inputError', className: styles.errorIcon, alt: 'Erro' }), _jsx("p", { className: styles.errorText, children: generalError })] })), _jsx("div", { className: styles.buttonsContainer, children: _jsxs("button", { type: 'button', onClick: handleSubmit, className: styles.confirmButton, disabled: !isFormValid || isLoading, "data-testid": `${dataTestId}-submit-button`, children: [isLoading && _jsx(Loading, { size: 'micro', className: styles.loadingSpinner }), t('pin-creation.confirm')] }) })] }));
        }
    };
    return (_jsx(Modal, { show: isOpen, keyboard: false, backdrop: 'static', onHide: () => { }, className: styles.modal, "data-testid": `${dataTestId}-modal`, backdropClassName: styles.backdrop, dialogClassName: styles.modalDialog, contentClassName: styles.modalContent, onEntered: step === 'form' ? handleModalEntered : undefined, children: _jsxs("div", { className: styles.pinCreationContainer, "data-testid": dataTestId, children: [step !== 'loading' && step !== 'form' && (_jsx("button", { type: 'button', onClick: onClose, className: styles.closeButton, "aria-label": t('pin-creation.close-aria-label'), "data-testid": step === 'success' ? `${dataTestId}-success-close` : `${dataTestId}-close`, children: _jsx(Icon, { name: 'actionClose', alt: t('pin-creation.close-aria-label') }) })), renderStepContent()] }) }));
}
export function PinCreationModal(props) {
    return (_jsx(TranslationProvider, { translations: sharedUiTranslations, children: _jsx(PinCreationModalBase, { ...props }) }));
}
export default PinCreationModal;
